import { useEffect, useRef } from "preact/hooks";
import { isAuthDisabled } from "../auth/apsConfig";
import {
  filterAccViewablesForPicker,
  pickDefaultAccViewable,
} from "../lib/accViewableFilter";
import { setApsDocument } from "../lib/apsDocumentRef";
import { resolveAccViewerUrn } from "../lib/resolveAccViewerUrn";
import { ModelDerivativeService } from "../services/modelDerivative";
import { useAccBrowseStore } from "../stores/accBrowseStore";
import { type ApsViewable, useAccModelStore } from "../stores/accModelStore";
import { useAccPipelineStore } from "../stores/accPipelineStore";
import { useAuthStore } from "../stores/authStore";
import { useDataManagementService } from "./useDataManagementService";

function parseViewables(doc: Autodesk.Viewing.Document): ApsViewable[] {
  const root = doc.getRoot();
  const nodes = root.search({ type: "geometry" }) ?? [];
  const out: ApsViewable[] = [];
  for (const node of nodes) {
    const data = node.data as { name?: string; role?: string; guid?: string };
    const id = data.guid ?? node.guid?.();
    if (!id) continue;
    const role = data.role ?? "3d";
    out.push({
      id,
      name: data.name ?? id,
      role,
      kind: role === "2d" ? "2d" : "3d",
    });
  }
  return out;
}

/**
 * When the user picks an ACC .rvt file, resolve its viewer URN and prepare
 * translation. Viewable enumeration happens after Document.load in the viewer hook.
 */
export function useAccLiveModel(): void {
  const dm = useDataManagementService();
  const selection = useAccBrowseStore(s => s.selection);
  const ensureAccessToken = useAuthStore(s => s.ensureAccessToken);
  const mdRef = useRef<ModelDerivativeService | null>(null);
  if (!mdRef.current) {
    mdRef.current = new ModelDerivativeService(ensureAccessToken);
  }

  useEffect(() => {
    if (isAuthDisabled() || !selection) {
      console.info("[Vera:ACC] No file selection — clearing model state");
      setApsDocument(null);
      useAccModelStore.getState().clear();
      useAccPipelineStore.getState().reset();
      return;
    }

    const key = `${selection.hubId}:${selection.projectId}:${selection.itemId}`;
    const st = useAccModelStore.getState();
    if (st.selectionKey === key && st.status === "ready" && st.viewerUrn) {
      console.info("[Vera:ACC] Reusing cached URN for", selection.fileName);
      return;
    }

    let cancelled = false;
    console.info("[Vera:ACC] File selected:", selection.fileName, key);
    useAccModelStore.getState().setLoading(selection);
    useAccPipelineStore
      .getState()
      .setStep("resolving", 5, `Preparing ${selection.fileName}…`);

    void (async () => {
      try {
        const viewerUrn = await resolveAccViewerUrn(
          dm,
          mdRef.current!,
          selection.projectId,
          selection.itemId,
          selection.hubRegion,
          (msg, pct) => {
            if (cancelled) return;
            useAccModelStore.getState().setProgress(msg);
            useAccPipelineStore
              .getState()
              .setStep(
                pct !== undefined && pct < 55 ? "translating" : "resolving",
                pct ?? 10,
                msg
              );
          }
        );
        if (cancelled) return;
        console.info("[Vera:ACC] URN ready — loading in viewer");
        useAccPipelineStore
          .getState()
          .setStep("loading_viewer", 58, "Opening model in viewer…");
        useAccModelStore.getState().setReady({
          viewerUrn,
          viewables: [],
          defaultViewableId: "",
          selection,
        });
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        useAccModelStore.getState().setError(msg);
        useAccPipelineStore.getState().setError(msg);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dm, selection, ensureAccessToken]);
}

/** Called from useAutodeskViewer after APS Document.load succeeds. */
export function registerApsViewables(
  doc: Autodesk.Viewing.Document,
  viewerUrn: string
): void {
  setApsDocument(doc);
  const st = useAccModelStore.getState();
  if (st.viewerUrn !== viewerUrn) return;
  const allViewables = parseViewables(doc);
  const viewables = filterAccViewablesForPicker(allViewables);
  const defaultId = pickDefaultAccViewable(viewables);
  console.info(
    `[Vera:ACC] Document loaded — ${allViewables.length} viewable(s), ${viewables.length} in picker:`,
    viewables.map(v => `${v.name} (${v.kind})`).join(", ")
  );
  if (viewables.length === 0) {
    useAccModelStore
      .getState()
      .setError(
        "Translation finished but no viewable sheets were found — wait for ACC to finish processing or re-upload the Revit model."
      );
    useAccPipelineStore
      .getState()
      .setError(
        "No viewable sheets in this model — translation may still be processing."
      );
    return;
  }
  useAccModelStore.setState({
    viewables,
    allViewables,
    currentViewableId: defaultId,
  });
}

export { ensureAccDocumentViewable } from "../lib/apsDocumentViewable";
